import { AxiosResponse } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import { useAppQuery } from 'core/query/hooks/useAppQuery';
import ApiClientManager from 'core/apiClient';
import { realmMigrationQueryKeys } from 'RealmModule/query/realmMigrationQueryKeys';
import {
  ScheduledMaintenanceState,
  ScheduledMaintenanceWindow,
  ClearScheduledMaintenancePayload,
  ClearScheduledMaintenanceResult,
  SetScheduledMaintenancePayload,
  SetScheduledMaintenanceResult,
  clearScheduledMaintenance,
  getScheduledMaintenance,
  setScheduledMaintenance,
} from 'RealmModule/services/scheduledMaintenanceApi';

/**
 * Every maintenance window on the flag, for this environment.
 *
 * There is no per-team read — the endpoint returns the whole rule set and the caller
 * picks its team out. Cheap enough (one GrowthBook admin read) and it is the only way to
 * see the two states that make a later `set` fail: the flag being disabled for this
 * environment, and a non-`force` rule existing anywhere on it.
 */
export const useScheduledMaintenance = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const query = useAppQuery<AxiosResponse<ScheduledMaintenanceState>>({
    queryKey: realmMigrationQueryKeys.list({ identifier: 'useScheduledMaintenance' }),
    queryFn: ({ token, buildConfig }) =>
      getScheduledMaintenance({ params: undefined, token, config: buildConfig() }),
    enabled,
    staleTime: 60 * 1000,
  });

  return {
    state: query.data?.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
};

/** The rule id galaxy writes for a (tenant, realm, team) window. */
const galaxyRuleId = ({ tenant, realmId, teamId }: TeamScope) =>
  `galaxy-maintenance:${tenant}:${realmId}:${teamId}`;

interface TeamScope {
  tenant: string;
  realmId: string;
  teamId: string;
}

/**
 * The galaxy-managed window for one team, if any.
 *
 * Matched on the rule CONDITION first, because that is what GrowthBook actually
 * evaluates, so a rule whose id was edited by hand still resolves. The id is checked as
 * a fallback: it encodes the same triple in a documented format, and without it a change
 * to the condition's shape on the server would make this silently return nothing — which
 * the rollback would read as "there was no previous window" and clear a rule it never
 * owned.
 */
export const findTeamWindow = (
  state: ScheduledMaintenanceState | undefined,
  scope: TeamScope,
): ScheduledMaintenanceWindow | undefined => {
  const { tenant, realmId, teamId } = scope;
  if (!state || !tenant || !realmId || !teamId) return undefined;

  const expectedId = galaxyRuleId(scope);

  return state.windows.find((window) => {
    if (!window.managedByGalaxy) return false;

    if (window.condition) {
      try {
        const condition = JSON.parse(window.condition);
        if (
          String(condition.tenantId) === tenant &&
          String(condition.realmId) === realmId &&
          String(condition.teamId) === teamId
        ) {
          return true;
        }
      } catch {
        // A hand-edited condition that is not JSON tells us nothing; fall through to the id.
      }
    }

    return window.ruleId === expectedId;
  });
};

export const useSetScheduledMaintenance = () => {
  const token = useSelector(selectAuthToken);

  const mutation = useMutation<
    AxiosResponse<SetScheduledMaintenanceResult>,
    AxiosResponse<{ message?: string }>,
    SetScheduledMaintenancePayload
  >({
    mutationFn: async (params) => {
      if (!token) {
        throw new Error('No auth token available');
      }
      // Pinned to the coordinates the window is FOR, not the realm the admin happens to be
      // viewing — the same reason pre-flight and start pin theirs. The body already carries
      // them, so anything else is a pair that disagrees with itself.
      return setScheduledMaintenance({
        params,
        token,
        config: {
          headers: ApiClientManager.buildUrlHeaders({
            tenantName: params.tenant,
            realmId: params.realmId,
          }),
        },
      });
    },
  });

  return {
    setWindow: mutation.mutateAsync,
    isSettingWindow: mutation.isPending,
  };
};

/**
 * Remove the galaxy-managed window for a team.
 *
 * Only usable while the team is still indexed in the realm the window was scoped to —
 * galaxy validates the team against its current realm, so once a migration completes and
 * the team re-indexes into the target this can no longer address the rule. That is why
 * the card uses it for one thing only: undoing a window it just set when the migration it
 * was set for turns out not to have started.
 */
export const useClearScheduledMaintenance = () => {
  const token = useSelector(selectAuthToken);

  const mutation = useMutation<
    AxiosResponse<ClearScheduledMaintenanceResult>,
    AxiosResponse<{ message?: string }>,
    ClearScheduledMaintenancePayload
  >({
    mutationFn: async (params) => {
      if (!token) {
        throw new Error('No auth token available');
      }
      // Pinned to the coordinates the window is FOR, not the realm the admin happens to be
      // viewing — the same reason pre-flight and start pin theirs. The body already carries
      // them, so anything else is a pair that disagrees with itself.
      return clearScheduledMaintenance({
        params,
        token,
        config: {
          headers: ApiClientManager.buildUrlHeaders({
            tenantName: params.tenant,
            realmId: params.realmId,
          }),
        },
      });
    },
  });

  return { clearWindow: mutation.mutateAsync, isClearingWindow: mutation.isPending };
};
